import { useQuery } from 'react-query';
import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import axios from 'utilities/axios';
import { useDebounce } from './useDebounce';
import useProfile from './useProfile';
import { formatNumber } from 'utilities/stringHelper';
import moment from 'moment-timezone';

const API = {
  CURRENCY: {
    CONVERT: 'currencies/convert',
  },
};

export const useCurrencyConversionApi = ({
  baseCurrencyIso = 'USD',
  targetCurrencyIso,
  amount,
  enabled = true,
}) => {
  return useQuery(
    ['currency-conversion', baseCurrencyIso, targetCurrencyIso, amount],
    () =>
      axios
        .post(API.CURRENCY.CONVERT, {
          baseCurrencyIso,
          targetCurrencyIso,
          amount,
        })
        .then((res) => res.data),
    {
      enabled: enabled && !!amount && amount > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

export const useCurrencyConversion = (amount) => {
  const intl = useIntl();
  const { data: profile } = useProfile();

  const debouncedAmount = useDebounce(amount, 500);

  const targetCurrency = profile?.currencyCode;
  const shouldShowConversion = !!targetCurrency && targetCurrency !== 'USD';

  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const handleTooltipToggle = () => {
    setTooltipVisible((prev) => !prev);
  };

  const { data: convertedAmount, isLoading: isConvertingCurrency } =
    useCurrencyConversionApi({
      baseCurrencyIso: 'USD',
      targetCurrencyIso: targetCurrency,
      amount: debouncedAmount,
      enabled: !!debouncedAmount && shouldShowConversion,
    });

  const simpleFormatUSD = useCallback(
    (value) => {
      const formattedValue = `$${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, false)} USD`;

      return formattedValue;
    },
    []
  );

  const formatWithConversion = useCallback(
    (value) => {
      const formattedValue = shouldShowConversion
        ? `$${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, false)} USD`
        : formatNumber(value);

      if (!shouldShowConversion) {
        return formattedValue;
      }

      if (isConvertingCurrency) {
        return `${formattedValue} (${intl.formatMessage({
          id: 'common.converting',
        })})`;
      }

      return `${formattedValue} (≈ $${formatNumber(
        convertedAmount?.convertedAmount || 0,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        false
      )} ${targetCurrency})`;
    },
    [
      intl,
      shouldShowConversion,
      isConvertingCurrency,
      convertedAmount,
      targetCurrency,
    ]
  );

  const formatWithConversionAndTooltip = useCallback(
    (value) => {
      const formattedValue = shouldShowConversion
        ? `$${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, false)} USD`
        : formatNumber(value);

      if (!shouldShowConversion) {
        return <span>{formattedValue}</span>;
      }

      if (isConvertingCurrency) {
        return (
          <span>
            {formattedValue} ({intl.formatMessage({ id: 'common.converting' })})
          </span>
        );
      }
      

      const rateText = `[1:${formatNumber(convertedAmount?.rate || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, false)}]`;
      const tooltipText = convertedAmount?.rateSyncedTime
        ? `Rate synced at: ${moment.tz(convertedAmount.rateSyncedTime, 'America/New_York').format('MMM DD, YYYY - HH:mm:ss')}`
        : 'Rate sync time not available';

      return (
        <span>
          {formattedValue}
          <br />
          <span style={{ fontSize: '0.7em'}}>
            (≈ $
          {formatNumber(
            convertedAmount?.convertedAmount || 0,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            false
          )} {targetCurrency}){' '}
            <span
              onClick={handleTooltipToggle}
              style={{ cursor: 'pointer', textDecoration: 'underline dotted', pointerEvents: 'auto' }}
            >
              {convertedAmount?.rate && rateText}
              {isTooltipVisible && (
                <div
                  style={{
                    position: 'absolute',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    padding: '5px',
                    borderRadius: '4px',
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                  }}
                >
                  {tooltipText}
                </div>
              )}
            </span>
          </span>
        </span>
      );
    },
    [
      intl,
      shouldShowConversion,
      isConvertingCurrency,
      convertedAmount,
      handleTooltipToggle,
      isTooltipVisible,
      targetCurrency,
    ]
  );

  return {
    targetCurrency,
    shouldShowConversion,
    isConvertingCurrency,
    convertedAmount: convertedAmount?.convertedAmount,
    simpleFormatUSD,
    formatWithConversion,
    formatWithConversionAndTooltip,
  };
};
